'use client';

import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, ArrowRight } from 'lucide-react';

const NAV_ITEMS = [
  { href: '/#services', label: 'Work' },
  { href: '/products/spacenest', label: 'SpaceNest' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
] as const;

function SandronLogo({ light = false }: { light?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold ${
          light
            ? 'bg-white text-sandron-ink-900'
            : 'bg-sandron-ink-900 text-white'
        }`}
      >
        S
      </span>
      <span
        className={`font-display text-lg font-bold tracking-tight ${
          light ? 'text-white' : 'text-sandron-ink-900'
        }`}
      >
        Sandron
        <span
          className={`ml-1 text-sm font-medium ${
            light ? 'text-white/60' : 'text-sandron-ink-500'
          }`}
        >
          Infotech
        </span>
      </span>
    </span>
  );
}

export { SandronLogo };

export function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const prevPathnameRef = useRef(pathname);

  // Scroll detection with rAF throttle
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY >= 24);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Body scroll lock
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  // Close drawer on route change — use startTransition to avoid
  // cascading render warnings (React 19 recommended pattern)
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      startTransition(() => setDrawerOpen(false));
    }
  }, [pathname]);

  // Focus trap + ESC
  useEffect(() => {
    if (!drawerOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        burgerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !drawerRef.current) return;

      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    burgerRef.current?.focus();
  }, []);

  const isActive = (href: string) => {
    if (href.startsWith('/#')) return pathname === '/';
    return pathname === href;
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,border-color] duration-200 ease-out motion-reduce:transition-none ${
        scrolled
          ? 'border-b border-sandron-ink-900/8 bg-white/[0.78] backdrop-blur-[14px] backdrop-saturate-[140%]'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-[clamp(20px,4vw,32px)]">
        {/* Logo */}
        <Link href="/" aria-label="Sandron Infotech home">
          <SandronLogo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 md:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`font-display text-[14.5px] font-medium transition-colors duration-150 hover:text-sandron-ink-900 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-spacenest-primary ${
                isActive(item.href)
                  ? 'font-semibold text-sandron-ink-900'
                  : 'text-sandron-ink-700'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <Link
          href="/contact"
          className="hidden items-center gap-2 rounded-lg bg-spacenest-primary px-5 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-[#256E48] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary md:inline-flex"
        >
          Let&apos;s talk
          <ArrowRight size={14} />
        </Link>

        {/* Mobile burger */}
        <button
          ref={burgerRef}
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-sandron-ink-900 hover:bg-sandron-ink-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary md:hidden"
          aria-expanded={drawerOpen}
          aria-controls="mobile-nav"
          aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setDrawerOpen((o) => !o)}
        >
          {drawerOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 motion-reduce:bg-black/30 md:hidden"
          style={{ animation: 'fadeIn 200ms ease-out' }}
          onClick={closeDrawer}
          aria-hidden
        />
      )}

      {/* Mobile drawer */}
      <div
        ref={drawerRef}
        id="mobile-nav"
        role="dialog"
        aria-label="Navigation menu"
        aria-modal="true"
        className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col bg-sandron-ink-50 p-6 shadow-2xl transition-transform duration-[280ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none md:hidden ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer close */}
        <div className="flex justify-end">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-sandron-ink-900 hover:bg-sandron-ink-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary"
            onClick={closeDrawer}
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        {/* Drawer nav */}
        <nav className="mt-8 flex flex-1 flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={closeDrawer}
              className={`rounded-lg px-4 py-3.5 font-display text-2xl font-semibold transition-colors hover:bg-sandron-ink-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary ${
                isActive(item.href)
                  ? 'text-sandron-ink-900'
                  : 'text-sandron-ink-700'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Drawer CTA */}
        <Link
          href="/contact"
          onClick={closeDrawer}
          className="mt-auto flex items-center justify-center gap-2 rounded-lg bg-spacenest-primary px-5 py-3.5 font-display text-base font-semibold text-white transition-colors hover:bg-[#256E48] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary"
        >
          Let&apos;s talk
          <ArrowRight size={16} />
        </Link>
      </div>
    </header>
  );
}
